<template>
    <TopNav />
    <div class="register container mt-5">
        <div class="row justify-content-center">
            <div>
                <form @submit.prevent="updateProfile" class="row">
                    <div class="mb-3 col-md-6">
                        <label for="firstName" class="form-label">First Name</label>
                        <input type="text" id="firstName" v-model="firstName" @blur="touched.firstName = true"
                            class="form-control" />
                        <small v-if="touched.firstName && !firstName" class="text-danger">First Name is
                            required.</small>
                    </div>
                    <div class="mb-3 col-md-6">
                        <label for="lastName" class="form-label">Last Name</label>
                        <input type="text" id="lastName" v-model="lastName" @blur="touched.lastName = true"
                            class="form-control" />
                        <small v-if="touched.lastName && !lastName" class="text-danger">Last Name is required.</small>
                    </div>
                    <div class="mb-3">
                        <label for="email" class="form-label">Email</label>
                        <input type="email" id="email" v-model="email" @blur="touched.email = true" class="form-control"
                            disabled />
                        <small v-if="touched.email && !email" class="text-danger">Email is required.</small>
                        <small v-else-if="touched.email && !isEmailValid" class="text-danger">Please enter a valid email
                            address.</small>
                    </div>
                    <div class="mb-3">
                        <label for="phone" class="form-label">Phone Number</label>
                        <vue-tel-input id="phone" v-model="phone" @blur="handleBlur('phone')" disabled
                            mode="international"></vue-tel-input>
                        <small v-if="touched.phone && !phone" class="text-danger">Phone is required.</small>
                        <small v-else-if="touched.phone && !isPhoneValid" class="text-danger">Phone Number must be at
                            least 10 characters.</small>
                    </div>
                    <div class="mb-3 col-md-6">
                        <label for="lic" class="form-label">LIC</label>
                        <input type="text" id="lic" v-model="lic" @blur="touched.lic = true" class="form-control"
                            disabled />
                        <small v-if="touched.lic && !lic" class="text-danger">LIC is required.</small>
                    </div>
                    <div class="mb-3 col-md-6">
                        <label for="npi" class="form-label">NPI</label>
                        <input type="text" id="npi" v-model="npi" @blur="touched.npi = true" class="form-control"
                            disabled />
                        <small v-if="touched.npi && !npi" class="text-danger">NPI is required.</small>
                    </div>
                    <div class="mb-3">
                        <label for="state" class="form-label">State</label>
                        <select v-model="selectedState" @blur="touched.selectedState = true" id="state"
                            class="form-select">
                            <option value="" disabled>Select a state</option>
                            <option v-for="state in states" :key="state.name" :value="state.name">
                                {{ state.name }}
                            </option>
                        </select>
                        <small v-if="touched.selectedState && !selectedState" class="text-danger">State is
                            required.</small>
                    </div>
                    <div class="mb-3">
                        <label for="clinicCode" class="form-label">Clinic Code</label>
                        <input type="text" id="clinicCode" v-model="clinicCode" @blur="touched.clinicCode = true"
                            class="form-control" disabled />
                        <small v-if="touched.clinicCode && !clinicCode" class="text-danger">Clinic Code is
                            required.</small>
                    </div>
                    <div class="mb-3">
                        <label for="specialty" class="form-label">Select your specialty</label>
                        <select v-model="selectedSpecialty" @blur="touched.selectedSpecialty = true" id="specialty"
                            class="form-select" disabled>
                            <option value="" disabled>Select a specialty</option>
                            <option v-for="specialty in specialties" :key="specialty.id" :value="specialty.id">
                                {{ specialty.name }}
                            </option>
                        </select>
                        <small v-if="touched.selectedSpecialty && !selectedSpecialty" class="text-danger">Specialty is
                            required.</small>
                    </div>
                    <button type="submit" class="btn  w-100" >Edit</button>
                </form>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useToast } from 'vue-toastification';
import states from '@/assets/states.json';
import { VueTelInput } from 'vue-tel-input';
import 'vue-tel-input/vue-tel-input.css';
import TopNav from '../layouts/topNav.vue'
import { getProfileDataService, updateProfileDataService } from '@/services/api/profileService';
import { getSpecialtyList } from "@/services/api/commonService";

const firstName = ref('');
const lastName = ref('');
const email = ref('');
const phone = ref('');
const lic = ref('');
const npi = ref('');
const touched = ref({
    firstName: false,
    lastName: false,
    email: false,
    phone: false,
    lic: false,
    npi: false,
    clinicCode: false,
    selectedSpecialty: false,
    selectedState: false,
});
const clinicCode = ref('' || 61145);
const selectedSpecialty = ref('');
const specialties = ref([]);
const selectedState = ref('');
const loading = ref(false);
const toast = useToast();
const token = localStorage.getItem('token')
const fetchSpecialties = async () => {
    const data = await getSpecialtyList();
    specialties.value = data;
};

onMounted(() => {
    fetchSpecialties();
    getProfileData()
});
const isEmailValid = computed(() => {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailPattern.test(email.value);
});
const isPhoneValid = computed(() => phone.value && phone.value.length >= 10);
const handleBlur = (field) => {
    touched.value[field] = true;
};
const getProfileData = async () => {
    loading.value = true;
    try {
        const response = await getProfileDataService(token);
        const data = response;
        // Assign response data to your fields
        firstName.value = data.first_name || '';
        lastName.value = data.last_name || '';
        email.value = data.email || '';
        phone.value = data.phone || '';
        lic.value = data.lic || '';
        npi.value = data.npl || '';
        selectedSpecialty.value = data.specialty_id || '';
        selectedState.value = data.state_name || '';
    } catch (error) {
        const statusCode = error.response?.status;
        error.value(`Server error ${statusCode}`);
        toast.error(`Server error ${statusCode}`);
    }
}
const updateProfile = async () => {
    loading.value = true;
    const payload = {
        first_name: firstName.value,
        last_name: lastName.value,
        state_name: selectedState.value,
    }
    const response = await updateProfileDataService(payload, token);
    toast.success(response.message);
    loading.value = false;
};
</script>

<style scoped>
.register {
    max-width: 80%;
    margin: 0 auto;
    padding: 2rem;
}

a {
    text-decoration: none;
    color: #518DB1;
    font-weight: bold;
}
button{
    background-color: #518DB1;
    color: white;
}
button:hover{
    background-color: #427493;
    color: white;
}

ul {
    list-style-type: none;
}
</style>